'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Calendar, Clock, ArrowRight, Search, X, Sparkles } from 'lucide-react'
import type { BlogPost } from '@/lib/supabase'

/* ------------------------------------------------------------------ */
/*  Helper                                                             */
/* ------------------------------------------------------------------ */

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/* ------------------------------------------------------------------ */
/*  Featured Card                                                      */
/* ------------------------------------------------------------------ */

function FeaturedCard({ post }: { post: BlogPost }) {
  return (
    <div
      className="anim-fade-in-up in-view"
    >
      <Link href={`/blog/${post.slug}`} className="group block">
        <article className="glass-card-premium relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-red-950/20">
          {/* Hero image background */}
          {post.hero_image_url && (
            <div className="absolute inset-0 z-0">
              <Image
                src={post.hero_image_url}
                alt=""
                fill
                priority
                fetchPriority="high"
                className="object-cover opacity-20 group-hover:opacity-25 transition-opacity duration-500"
                sizes="(max-width: 1280px) 100vw, 1280px"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/80 to-zinc-950/60" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
            </div>
          )}

          <div className="relative z-10 p-8 md:p-12">
            {/* Featured badge */}
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold uppercase tracking-wider bg-red-600/20 text-red-400 rounded-full border border-red-600/30">
                <Sparkles size={10} />
                Featured
              </span>

              {/* Categories */}
              {post.categories && post.categories.length > 0 && (
                <>
                  {post.categories.map((cat) => (
                    <span
                      key={cat.id}
                      className="px-3 py-1 text-xs font-medium bg-white/[0.04] text-zinc-400 rounded-full border border-white/[0.06]"
                    >
                      {cat.name}
                    </span>
                  ))}
                </>
              )}
            </div>

            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-red-400 transition-colors duration-300 leading-tight">
              {post.title}
            </h2>

            {/* Excerpt */}
            <p className="text-zinc-400 mb-6 max-w-3xl leading-relaxed line-clamp-3">
              {post.excerpt || post.meta_description}
            </p>

            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500">
              <span className="flex items-center gap-1.5">
                <Calendar size={14} />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={14} />
                {post.reading_time_minutes} min read
              </span>
              <span className="ml-auto flex items-center gap-1.5 text-red-400 font-medium group-hover:gap-2.5 transition-all duration-300">
                Read Article <ArrowRight size={14} />
              </span>
            </div>
          </div>
        </article>
      </Link>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Grid Card                                                          */
/* ------------------------------------------------------------------ */

function BlogCard({ post, index }: { post: BlogPost; index: number }) {
  return (
    <div
      style={{
        opacity: 0,
        transform: 'translateY(30px)',
        animation: `blog-card-enter 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${index * 0.08}s forwards`,
      }}
    >
      <Link href={`/blog/${post.slug}`} className="group block h-full">
        <article className="glass-card-premium h-full flex flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-950/10">
          {/* Colored accent strip */}
          <div className="h-[2px] w-full bg-gradient-to-r from-red-600 to-red-500 opacity-60 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="p-6 flex flex-col flex-1">
            {/* Categories */}
            {post.categories && post.categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.categories.map((cat) => (
                  <span
                    key={cat.id}
                    className="px-2.5 py-0.5 text-xs font-medium bg-red-600/10 text-red-400 rounded-full border border-red-600/20"
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h2 className="text-lg font-semibold text-white mb-3 group-hover:text-red-400 transition-colors duration-300 line-clamp-2 leading-snug">
              {post.title}
            </h2>

            {/* Excerpt */}
            <p className="text-sm text-zinc-400 mb-4 line-clamp-3 flex-1 leading-relaxed">
              {post.excerpt || post.meta_description}
            </p>

            {/* Meta footer */}
            <div className="flex items-center justify-between text-xs text-zinc-500 pt-4 border-t border-white/[0.04]">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(post.published_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {post.reading_time_minutes} min
                </span>
              </div>
              <ArrowRight
                size={14}
                className="text-zinc-600 group-hover:text-red-400 group-hover:translate-x-1 transition-all duration-300"
              />
            </div>
          </div>
        </article>
      </Link>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Grid Component                                                */
/* ------------------------------------------------------------------ */

export default function BlogGrid({ posts }: { posts: BlogPost[] }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [searchFocused, setSearchFocused] = useState(false)

  /* Extract unique categories */
  const allCategories = useMemo(() => {
    const cats = new Map<string, { id: string; name: string }>()
    posts.forEach((post) => {
      post.categories?.forEach((cat) => {
        if (!cats.has(cat.id)) cats.set(cat.id, cat)
      })
    })
    return Array.from(cats.values())
  }, [posts])

  /* Filter posts */
  const filteredPosts = useMemo(() => {
    let result = posts
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          (p.excerpt || '').toLowerCase().includes(q) ||
          (p.meta_description || '').toLowerCase().includes(q)
      )
    }
    if (activeCategory) {
      result = result.filter((p) =>
        p.categories?.some((c) => c.id === activeCategory)
      )
    }
    return result
  }, [posts, searchQuery, activeCategory])

  const featured = filteredPosts[0]
  const rest = filteredPosts.slice(1)
  const isFiltering = searchQuery.trim() !== '' || activeCategory !== null

  return (
    <>
      {/* ============================================================ */}
      {/*  Search & Filter Bar                                         */}
      {/* ============================================================ */}
      <div className="section-inner mb-10">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Search input */}
          <div className="relative flex-1 max-w-md">
            <Search
              size={16}
              className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                searchFocused ? 'text-red-400' : 'text-zinc-500'
              }`}
            />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              className={`w-full pl-10 pr-10 py-3 bg-white/[0.03] backdrop-blur-sm border rounded-xl text-white text-sm placeholder-zinc-500 focus:outline-none transition-all duration-300 ${
                searchFocused
                  ? 'border-red-500/40 ring-2 ring-red-500/10 shadow-lg shadow-red-950/10'
                  : 'border-white/[0.06] hover:border-white/[0.1]'
              }`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-zinc-500 hover:text-white rounded-full hover:bg-white/[0.06] transition-colors cursor-pointer"
                style={{
                  transition: 'opacity 0.15s ease, transform 0.15s ease',
                }}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Category filter pills */}
          {allCategories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory(null)}
                className={`px-3.5 py-1.5 text-xs font-medium rounded-full border transition-all duration-200 cursor-pointer active:scale-95 ${
                  activeCategory === null
                    ? 'bg-red-600/15 text-red-400 border-red-600/30 shadow-sm shadow-red-950/20'
                    : 'bg-transparent text-zinc-400 border-white/[0.06] hover:border-white/[0.12] hover:text-zinc-300'
                }`}
              >
                All
              </button>
              {allCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() =>
                    setActiveCategory(activeCategory === cat.id ? null : cat.id)
                  }
                  className={`px-3.5 py-1.5 text-xs font-medium rounded-full border transition-all duration-200 cursor-pointer active:scale-95 ${
                    activeCategory === cat.id
                      ? 'bg-red-600/15 text-red-400 border-red-600/30 shadow-sm shadow-red-950/20'
                      : 'bg-transparent text-zinc-400 border-white/[0.06] hover:border-white/[0.12] hover:text-zinc-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Result count */}
        <div
          style={{
            overflow: 'hidden',
            maxHeight: isFiltering ? '3rem' : '0',
            marginTop: isFiltering ? '16px' : '0',
            opacity: isFiltering ? 1 : 0,
            transition: 'max-height 0.25s ease, margin-top 0.25s ease, opacity 0.25s ease',
          }}
        >
          <p className="text-sm text-zinc-500">
            <span className="inline-block text-zinc-300 font-medium mr-1">
              {filteredPosts.length}
            </span>
            article{filteredPosts.length !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>

      {/* ============================================================ */}
      {/*  Posts                                                        */}
      {/* ============================================================ */}
      {filteredPosts.length > 0 ? (
        <>
          {/* Featured post */}
          {featured && (
            <div className="section-inner mb-8">
              <FeaturedCard post={featured} />
            </div>
          )}

          {/* Grid */}
          {rest.length > 0 && (
            <div className="section-inner">
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
              >
                {rest.map((post, i) => (
                  <BlogCard key={post.id} post={post} index={i} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* ========================================================== */
        /*  Empty state                                                */
        /* ========================================================== */
        <div className="section-inner text-center py-24">
          <div className="anim-fade-in-up in-view">
            {/* Pulsing search icon */}
            <div
              className="mx-auto mb-5 w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center"
              style={{
                animation: 'blog-pulse 2.5s ease-in-out infinite',
              }}
            >
              <Search size={22} className="text-zinc-500" />
            </div>

            <p className="text-zinc-400 text-lg font-medium mb-1">
              No articles found
            </p>
            <p className="text-zinc-500 text-sm mb-5">
              {isFiltering
                ? 'Try adjusting your search or filter criteria.'
                : 'No posts published yet. Check back soon!'}
            </p>

            {isFiltering && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setActiveCategory(null)
                }}
                className="text-red-400 text-sm font-medium hover:text-red-300 transition-colors cursor-pointer"
              >
                Clear all filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Inline keyframes for blog grid animations */}
      <style jsx>{`
        @keyframes blog-card-enter {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes blog-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.4;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.7;
          }
        }
      `}</style>
    </>
  )
}
